import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, DragEndEvent, useDroppable, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Shirt } from 'lucide-react';

interface LaundryItem {
  id: string;
  stage: 'dirty' | 'wash' | 'dry' | 'gym';
}

const stages: Array<{ id: LaundryItem['stage']; label: string }> = [
  { id: 'dirty', label: 'Грязное белье' },
  { id: 'wash', label: 'Стирка' },
  { id: 'dry', label: 'Сушка' },
  { id: 'gym', label: 'Зал' },
];

const GACHI_MUCHI_QUOTES = [
  "I'll show you who's boss of this gym!",
  "Deep Dark Fantasies...",
  "Take it like a man!",
  "Fisting is 300 bucks!",
  "Dungeon Master!",
  "Ass we can!",
];

// Simple confetti particles
const ConfettiParticle = ({ delay }: { delay: number }) => (
  <motion.div
    initial={{ opacity: 1, y: 0, x: 0, rotate: 0 }}
    animate={{
      opacity: [1, 1, 0],
      y: [0, -100, -200],
      x: [0, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 150],
      rotate: [0, Math.random() * 360],
    }}
    transition={{ duration: 2, delay, ease: 'easeOut' }}
    className="fixed pointer-events-none"
    style={{
      left: `${Math.random() * 100}%`,
      top: '50%',
    }}
  >
    <div className="text-3xl">{['🎉', '✨', '🎊', '⭐', '🌟'][Math.floor(Math.random() * 5)]}</div>
  </motion.div>
);

const DraggableSock = ({ id, stage }: LaundryItem) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: { stage },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing transition-transform hover:scale-110 active:scale-90"
    >
      <Shirt className="w-8 h-8 text-[#9B9A97]" />
    </div>
  );
};

const DroppableStage = ({ stageId, children }: { stageId: LaundryItem['stage']; children: React.ReactNode }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: stageId,
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-wrap gap-2 min-h-[3rem] p-3 bg-[#202020] rounded-sm border transition-colors ${
        isOver ? 'border-[#6A8759] bg-[#2A2A2A]' : 'border-[#2F2F2F]'
      }`}
    >
      {children}
    </div>
  );
};

export function LaundryPipeline() {
  const [items, setItems] = useState<LaundryItem[]>([
    { id: '1', stage: 'dirty' },
    { id: '2', stage: 'dirty' },
    { id: '3', stage: 'dirty' },
  ]);

  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationQuote, setCelebrationQuote] = useState('');
  const celebrationTimeoutRef = useRef<NodeJS.Timeout>();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const draggedItem = items.find((item) => item.id === active.id);
    const targetStage = over.id as LaundryItem['stage'];

    if (!draggedItem || draggedItem.stage === targetStage) return;

    // Check if we're moving to gym (final stage)
    const isMovingToGym = targetStage === 'gym';

    setItems((prevItems) => {
      const updatedItems = prevItems.map((item) =>
        item.id === active.id ? { ...item, stage: targetStage } : item
      );

      // Check if all items are now in gym
      if (isMovingToGym && updatedItems.every((item) => item.stage === 'gym')) {
        // Trigger celebration
        setCelebrationQuote(GACHI_MUCHI_QUOTES[Math.floor(Math.random() * GACHI_MUCHI_QUOTES.length)]);
        setShowCelebration(true);

        // Auto-reset after 3 seconds
        if (celebrationTimeoutRef.current) {
          clearTimeout(celebrationTimeoutRef.current);
        }
        celebrationTimeoutRef.current = setTimeout(() => {
          setItems(
            updatedItems.map((item) => ({ ...item, stage: 'dirty' }))
          );
          setShowCelebration(false);
        }, 3000);
      }

      return updatedItems;
    });
  };

  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
      }
    };
  }, []);

  const getItemsForStage = (stage: LaundryItem['stage']) => {
    return items.filter((item) => item.stage === stage);
  };

  return (
    <div className="w-full space-y-6 overflow-visible relative">
      {/* Celebration overlay */}
      <AnimatePresence>
        {showCelebration && (
          <>
            {/* Confetti */}
            {Array.from({ length: 20 }).map((_, i) => (
              <ConfettiParticle key={i} delay={i * 0.05} />
            ))}

            {/* Gachi Muchi quote */}
            <motion.div
              key="quote"
              initial={{ opacity: 0, scale: 0.5, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.5, y: -50 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none"
            >
              <div className="bg-gradient-to-r from-[#FF1493] to-[#FF69B4] text-white px-8 py-6 rounded-xl shadow-2xl text-center">
                <p className="text-3xl font-bold animate-bounce">{celebrationQuote}</p>
                <p className="text-sm text-white/80 mt-2">🎉 ВСЯ ОДЕЖДА В ЗАЛЕ! 🎉</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <DndContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-2">
          {stages.map((stage, idx) => (
            <div key={stage.id} className="flex flex-col overflow-visible">
              {/* Header - always at top */}
              <h4 className="text-white/90 font-medium mb-2 truncate">{stage.label}</h4>
              
              {/* Drop zone row with arrow */}
              <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 lg:gap-2 overflow-visible">
                <div className="flex-1 w-full min-w-0 overflow-visible">
                  <DroppableStage stageId={stage.id}>
                    {getItemsForStage(stage.id).map((item) => (
                      <DraggableSock key={item.id} {...item} />
                    ))}
                  </DroppableStage>
                </div>
                {idx < stages.length - 1 && (
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="hidden lg:flex text-[#9B9A97] text-2xl flex-shrink-0 justify-center relative z-10 items-center w-6"
                  >
                    →
                  </motion.div>
                )}
                {idx < stages.length - 1 && (
                  <motion.div
                    animate={{ y: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="lg:hidden text-[#9B9A97] text-xl flex-shrink-0 justify-center relative z-10 w-full text-center"
                  >
                    ↓
                  </motion.div>
                )}
              </div>
            </div>
          ))}
        </div>
      </DndContext>
    </div>
  );
}
